/**
 * Reexport desde `@trazit/utils-cc-apis` (implementación en el paquete).
 * Contrato awareness: `procedure_view`, `badge_hover`, `data[]` por entrada en `awareness_queries`.
 */

import { evaluateConditions, normalizeConditionRules } from './condition-rules-eval.js';

/** Evita N× JSON.parse(userSession) en un solo render del menú (awareness por ítem). */
let _userSessionParseCache = { raw: null, parsed: null };

function getParsedUserSessionFromStorage() {
  const raw = sessionStorage.getItem('userSession');
  const cacheKey = raw == null ? '' : raw;
  if (_userSessionParseCache.raw === cacheKey && _userSessionParseCache.parsed != null) {
    return _userSessionParseCache.parsed;
  }
  try {
    const parsed = JSON.parse(raw || '{}');
    _userSessionParseCache = { raw: cacheKey, parsed };
    return parsed;
  } catch {
    const empty = {};
    _userSessionParseCache = { raw: cacheKey, parsed: empty };
    return empty;
  }
}

/** Resultados awareness memoizados por snapshot de `userSession` (invalida al cambiar el string en sessionStorage). */
let _awarenessMemo = {
  sessionKey: '',
  count: /** @type {Map<string, number>} */ (new Map()),
  iconStrip: /** @type {Map<string, Array<{ count: number, tone: ''|'warning'|'alert', tooltip: string }>>} */ (new Map()),
  tone: /** @type {Map<string, ''|'warning'|'alert'>} */ (new Map())
};

function getAwarenessMemoBuckets() {
  const k = sessionStorage.getItem('userSession') ?? '';
  if (_awarenessMemo.sessionKey !== k) {
    _awarenessMemo = {
      sessionKey: k,
      count: new Map(),
      iconStrip: new Map(),
      tone: new Map()
    };
  }
  return _awarenessMemo;
}

function awarenessMemoKeyProcLp(procInstanceName, lpFrontendPageName) {
  return `${String(procInstanceName).toLowerCase()}\x1e${String(lpFrontendPageName).trim().toLowerCase()}`;
}

export {
  GET_PROCEDURE_AWARENESS_QUERIES_ACTION,
  addProcedureAwarenessQuery,
  updateProcedureAwarenessQuery,
  removeProcedureAwarenessQuery,
  postAuthenticationAction,
  buildAuthenticationActionRequest,
  fetchProcedureAwarenessQueries,
  ensureAwarenessQueryEntriesHaveDataArray,
  getAwarenessQueryProcedureView,
  getAwarenessQueryBadgeHoverTemplate
} from '@trazit/utils-cc-apis';

/**
 * El backend a veces devuelve `procedures_list.procedures_list.procedures` en lugar de
 * `procedures_list.procedures`. Unifica para que exista siempre `procedures` en el primer nivel.
 *
 * No escribe `awareness_queries` por sí sola: el merge y {@link applyProcedureAwarenessFromGetPayload}
 * rellenan procedimientos / `procedure_awareness_queries` según el cuerpo del GET.
 *
 * @param {Record<string, unknown>|null|undefined} pl
 * @returns {Record<string, unknown>|null|undefined}
 */
export function normalizeProceduresListShape(pl) {
  if (!pl || typeof pl !== 'object') return pl;
  // Si `procedures` ya está en el primer nivel, no hay doble anidación que aplanar (no se lee `pl.procedures_list` abajo).
  if (Array.isArray(pl.procedures)) return pl;
  const inner = pl.procedures_list;
  if (inner && typeof inner === 'object' && Array.isArray(inner.procedures)) {
    return { ...pl, procedures: inner.procedures };
  }
  return pl;
}

/**
 * Lista de procedimientos sea cual sea la forma anidada guardada en sesión.
 * @param {Record<string, unknown>} session
 * @returns {unknown[]|null}
 */
export function getSessionProceduresArray(session) {
  const pl = session?.procedures_list;
  if (!pl || typeof pl !== 'object') return null;
  if (Array.isArray(pl.procedures)) return pl.procedures;
  const inner = pl.procedures_list;
  if (inner && typeof inner === 'object' && Array.isArray(inner.procedures)) {
    return inner.procedures;
  }
  return null;
}

/**
 * @param {Record<string, unknown>} session
 * @param {unknown[]} newProcedures
 */
export function setSessionProceduresArray(session, newProcedures) {
  const pl = session.procedures_list;
  if (!pl || typeof pl !== 'object') return;
  if (Array.isArray(pl.procedures)) {
    session.procedures_list = { ...pl, procedures: newProcedures };
    return;
  }
  if (pl.procedures_list && Array.isArray(pl.procedures_list.procedures)) {
    session.procedures_list = {
      ...pl,
      procedures_list: { ...pl.procedures_list, procedures: newProcedures }
    };
  }
}

/**
 * El GET de awareness (y a veces el de lista de procedimientos) puede traer `awareness_queries`
 * solo dentro de `procedures_list.procedures[]`, sin `procedure_awareness_queries` en la raíz.
 * Copia esos queries a la sesión y, si hace falta, rellena `procedure_awareness_queries` para el menú/merge.
 *
 * @param {Record<string, unknown>} session - userSession (mutado in-place)
 * @param {Record<string, unknown>} data - cuerpo JSON del GET (p. ej. respuesta awareness)
 */
export function applyProcedureAwarenessFromGetPayload(session, data) {
  if (!session || !data || typeof data !== 'object') return;
  const rawPl = data.procedures_list;
  if (!rawPl || typeof rawPl !== 'object') return;
  const plNorm = normalizeProceduresListShape(rawPl);
  const fromApi = getSessionProceduresArray({ procedures_list: plNorm });
  if (!Array.isArray(fromApi) || fromApi.length === 0) return;

  const byApi = new Map();
  for (const ap of fromApi) {
    const k = String(ap?.procInstanceName ?? ap?.proc_instance_name ?? '').toLowerCase();
    if (!k) continue;
    const aq = ap?.awareness_queries ?? ap?.awareness_data;
    if (!Array.isArray(aq) || aq.length === 0) continue;
    byApi.set(k, aq);
  }
  if (byApi.size === 0) return;

  const current = getSessionProceduresArray(session);
  if (Array.isArray(current)) {
    const merged = current.map((p) => {
      const k = String(p?.procInstanceName ?? p?.proc_instance_name ?? '').toLowerCase();
      if (!byApi.has(k)) return p;
      return { ...p, awareness_queries: byApi.get(k) };
    });
    setSessionProceduresArray(session, merged);
  }

  const paq = session.procedure_awareness_queries;
  const hasRootBlocks =
    Array.isArray(paq) &&
    paq.length > 0 &&
    paq[0] &&
    typeof paq[0] === 'object' &&
    ('procInstanceName' in paq[0] || 'proc_instance_name' in paq[0]);
  if (!hasRootBlocks) {
    const blocks = [];
    for (const ap of fromApi) {
      const pin = ap?.procInstanceName ?? ap?.proc_instance_name;
      const aq = ap?.awareness_queries ?? ap?.awareness_data;
      if (pin == null || !Array.isArray(aq) || aq.length === 0) continue;
      blocks.push({
        procInstanceName: pin,
        awareness_queries: aq
      });
    }
    if (blocks.length) {
      session.procedure_awareness_queries = blocks;
    }
  }
}

/**
 * Tras persistir `procedure_awareness_queries` / `awareness_indicators` (bloques por procedimiento),
 * copia `awareness_queries` dentro de cada entrada de `procedures_list.procedures` (como `master_data`),
 * para que el menú y la consola vean los datos en el mismo objeto de procedimiento.
 * @param {Record<string, unknown>} session - userSession (mutado in-place + procedures_list reemplazado)
 * @returns {Record<string, unknown>}
 */
export function mergeAwarenessBlocksIntoProceduresList(session) {
  if (!session || typeof session !== 'object') return session;
  const procedures = getSessionProceduresArray(session);
  if (!Array.isArray(procedures)) return session;

  const blocks = pickProcedureAwarenessBlocks(session);
  if (!blocks) return session;

  const byProc = new Map();
  for (const block of blocks) {
    if (!block || typeof block !== 'object') continue;
    const pin = block.procInstanceName ?? block.proc_instance_name;
    if (pin == null) continue;
    const q = block.awareness_queries;
    byProc.set(String(pin).toLowerCase(), Array.isArray(q) ? q : []);
  }
  if (byProc.size === 0) return session;

  const mapped = procedures.map((p) => {
    const key = String(p?.procInstanceName ?? p?.proc_instance_name ?? '').toLowerCase();
    if (!key || !byProc.has(key)) return p;
    const aq = byProc.get(key);
    return { ...p, awareness_queries: aq };
  });
  setSessionProceduresArray(session, mapped);
  return session;
}

/**
 * @param {Record<string, unknown>} session
 * @returns {unknown[]|null}
 */
function pickProcedureAwarenessBlocks(session) {
  const paq = session.procedure_awareness_queries;
  if (Array.isArray(paq) && paq.length > 0) {
    const first = paq[0];
    if (
      first &&
      typeof first === 'object' &&
      ('procInstanceName' in first || 'proc_instance_name' in first)
    ) {
      return paq;
    }
  }
  const ind = session.awareness_indicators;
  if (Array.isArray(ind) && ind.length > 0) {
    const first = ind[0];
    if (
      first &&
      typeof first === 'object' &&
      ('procInstanceName' in first || 'proc_instance_name' in first)
    ) {
      return ind;
    }
  }
  return deriveProcedureAwarenessBlocksFromEmbeddedProcedures(session);
}

/**
 * Cuando solo existen queries embebidas en `procedures_list.procedures[]` (sin claves raíz).
 * @param {Record<string, unknown>} session
 * @returns {unknown[]|null}
 */
function deriveProcedureAwarenessBlocksFromEmbeddedProcedures(session) {
  const procedures = getSessionProceduresArray(session);
  if (!Array.isArray(procedures)) return null;
  const blocks = [];
  for (const p of procedures) {
    const pin = p?.procInstanceName ?? p?.proc_instance_name;
    const aq = p?.awareness_queries ?? p?.awareness_data;
    if (pin == null || !Array.isArray(aq) || aq.length === 0) continue;
    blocks.push({ procInstanceName: pin, awareness_queries: aq });
  }
  return blocks.length ? blocks : null;
}

// ─── Contadores awareness (compartidos: procedures-menu, welcome / tr-default) ───

/**
 * Valores reservados de `procedure_view` para badges en franjas de iconos (menú / welcome).
 * Deben coincidir con `type`/`windowType` `iconbuttonup` / `iconbuttondown` en vistas.
 */
export const AWARENESS_VIEW_ICONBUTTON_UP = 'iconbuttonup';
export const AWARENESS_VIEW_ICONBUTTON_DOWN = 'iconbuttondown';

/** @param {unknown} a */
export function awarenessEntryProcedureView(a) {
  if (!a || typeof a !== 'object') return '';
  const v =
    a.procedure_view ??
    a.procedureView ??
    a.view_name ??
    a.viewName ??
    a.lp_frontend_page_name ??
    '';
  return String(v).trim().toLowerCase();
}

/** @param {unknown} q */
export function awarenessEntryDataLength(q) {
  if (!q || typeof q !== 'object') return 0;
  const pick = q.data ?? q.rows ?? q.result ?? q.records;
  if (Array.isArray(pick)) return pick.length;
  if (typeof pick === 'string') {
    try {
      const p = JSON.parse(pick);
      return Array.isArray(p) ? p.length : 0;
    } catch (_) {
      return 0;
    }
  }
  return 0;
}

/** Filas `data` de una entrada awareness (misma resolución que {@link awarenessEntryDataLength}). */
export function awarenessEntryDataArray(q) {
  if (!q || typeof q !== 'object') return [];
  let pick = q.data ?? q.rows ?? q.result ?? q.records;
  if (typeof pick === 'string') {
    try {
      pick = JSON.parse(pick);
    } catch (_) {
      return [];
    }
  }
  return Array.isArray(pick) ? pick : [];
}

/**
 * Contexto para `badge_visibility_rules`: `rowCount` / `dataLength` + campos de la primera fila
 * (la primera fila no pisa `rowCount`). Alineado con operadores MAYÚSCULAS en `condition-rules-eval.js`.
 *
 * @param {Record<string, unknown>} entry
 * @returns {Record<string, unknown>}
 */
export function buildAwarenessBadgeVisibilityContext(entry) {
  const rows = awarenessEntryDataArray(entry);
  const rowCount = rows.length;
  const first =
    rows[0] && typeof rows[0] === 'object' && !Array.isArray(rows[0]) ? { ...rows[0] } : {};
  return {
    ...first,
    rowCount,
    dataLength: rowCount
  };
}

/**
 * Si el badge debe mostrarse para esta entrada.
 * - Sin `badge_visibility_rules` (o vacío): solo si hay al menos una fila (`rowCount > 0`).
 * - Con reglas: AND de condiciones `{ field|property, operator, value }` sobre el contexto anterior.
 *
 * @param {Record<string, unknown>} entry
 * @returns {boolean}
 */
export function awarenessBadgeVisibilityMatches(entry) {
  if (!entry || typeof entry !== 'object') return false;
  const rules = entry.badge_visibility_rules ?? entry.badgeVisibilityRules;
  const ctx = buildAwarenessBadgeVisibilityContext(entry);
  const rowCount = Number(ctx.rowCount) || 0;
  if (!Array.isArray(rules) || rules.length === 0) {
    return rowCount > 0;
  }
  const norm = normalizeConditionRules(rules);
  if (norm.length === 0) {
    return rowCount > 0;
  }
  return evaluateConditions(norm, ctx);
}

/**
 * @param {unknown} awarenessQueries
 * @param {string} lpFrontendPageName
 */
export function countAwarenessQueryRows(awarenessQueries, lpFrontendPageName) {
  if (!Array.isArray(awarenessQueries) || !lpFrontendPageName) return 0;
  const want = String(lpFrontendPageName).trim().toLowerCase();
  const q = awarenessQueries.find((a) => a && awarenessEntryProcedureView(a) === want);
  if (!q) return 0;
  if (!awarenessBadgeVisibilityMatches(q)) return 0;
  return awarenessEntryDataLength(q);
}

/** @param {unknown} p @param {string} procInstanceName */
export function procInstanceNameMatches(p, procInstanceName) {
  if (!p || !procInstanceName) return false;
  const want = String(procInstanceName).toLowerCase();
  const a = p.procInstanceName ?? p.proc_instance_name;
  return a != null && String(a).toLowerCase() === want;
}

/**
 * Filas awareness para procInstanceName + lp (vista). Igual que procedures-menu.getAwarenessDataCount.
 * @param {string} procInstanceName
 * @param {string} lpFrontendPageName
 */
export function getAwarenessDataCount(procInstanceName, lpFrontendPageName) {
  if (!procInstanceName || !lpFrontendPageName) return 0;
  const memo = getAwarenessMemoBuckets().count;
  const mk = awarenessMemoKeyProcLp(procInstanceName, lpFrontendPageName);
  if (memo.has(mk)) return memo.get(mk) ?? 0;
  try {
    const us = getParsedUserSessionFromStorage();

    let nList = 0;
    const procedures = getSessionProceduresArray(us);
    if (Array.isArray(procedures)) {
      const procFromList = procedures.find((p) => procInstanceNameMatches(p, procInstanceName));
      const aqEmbedded = procFromList?.awareness_queries ?? procFromList?.awareness_data;
      nList = countAwarenessQueryRows(aqEmbedded, lpFrontendPageName);
    }

    let nBlocks = 0;
    const blocks = Array.isArray(us.procedure_awareness_queries)
      ? us.procedure_awareness_queries
      : Array.isArray(us.awareness_indicators)
        ? us.awareness_indicators
        : null;
    if (Array.isArray(blocks)) {
      const procEntry = blocks.find((p) => procInstanceNameMatches(p, procInstanceName));
      nBlocks = countAwarenessQueryRows(procEntry?.awareness_queries, lpFrontendPageName);
    }

    const out = Math.max(nList, nBlocks);
    memo.set(mk, out);
    return out;
  } catch (_) {
    memo.set(mk, 0);
    return 0;
  }
}

/**
 * @param {unknown} rawSubProc
 * @param {unknown} _def
 */
function normalizeMenuSubViewEntry(rawSubProc, _def) {
  let subProc = rawSubProc;
  if (rawSubProc && typeof rawSubProc === 'object' && !Array.isArray(rawSubProc)) {
    const keys = Object.keys(rawSubProc);
    if (keys.length === 1 && rawSubProc[keys[0]] && typeof rawSubProc[keys[0]] === 'object') {
      const code = keys[0];
      subProc = {
        ...rawSubProc[keys[0]],
        name: rawSubProc[keys[0]].name ?? code,
        lp_frontend_page_filter: rawSubProc[keys[0]].lp_frontend_page_filter ?? code
      };
    }
  }
  return subProc;
}

/** @param {Record<string, unknown>|null|undefined} def */
function getSubViewIconList(def) {
  if (Array.isArray(def?.icons) && def.icons.length) return def.icons;
  const svf = def?.json_model?.subViewFilter;
  if (Array.isArray(svf) && svf.length) return svf;
  if (svf && typeof svf === 'object' && !Array.isArray(svf)) {
    return Object.values(svf).filter(Boolean);
  }
  return [];
}

/** Vista lógica para correlacionar con `procedure_view`. */
function resolveItemLpFrontendPageName(def) {
  return (
    def?.lp_frontend_page_name ||
    def?.json_model?.lp_frontend_page_name ||
    def?.view_name ||
    ''
  );
}

/**
 * Contador para una fila de menú o tarjeta welcome (`new_definition` / def).
 * Incluye vista base + subViewFilter (misma lógica que procedures-menu).
 *
 * @param {string} procInstanceName
 * @param {Record<string, unknown>|null|undefined} def
 */
export function getAwarenessDataCountForMenuDefinition(procInstanceName, def) {
  if (!procInstanceName || !def) return 0;
  const subs = getSubViewIconList(def);
  const baseLp = resolveItemLpFrontendPageName(def);
  if (subs.length === 0) {
    return baseLp ? getAwarenessDataCount(procInstanceName, baseLp) : 0;
  }
  const seen = new Set();
  let total = 0;
  if (baseLp) {
    const dk = String(baseLp).toLowerCase();
    seen.add(dk);
    total += getAwarenessDataCount(procInstanceName, baseLp);
  }
  for (const raw of subs) {
    const sub = normalizeMenuSubViewEntry(raw, def);
    const lp = sub?.lp_frontend_page_name || baseLp;
    const dedupe = String(lp || '').toLowerCase();
    if (!lp || seen.has(dedupe)) continue;
    seen.add(dedupe);
    total += getAwarenessDataCount(procInstanceName, lp);
  }
  return total;
}

/** @param {''|'warning'|'alert'} a @param {''|'warning'|'alert'} b */
function worseBadgeTone(a, b) {
  const r = { '': 0, warning: 1, alert: 2 };
  return (r[b] ?? 0) > (r[a] ?? 0) ? b : a;
}

/**
 * Normaliza severidad desde backend (tabla / JSON awareness).
 * `''` = informativo (azul). `warning` / `alert` = estilos existentes del menú.
 * @param {unknown} raw
 * @returns {''|'warning'|'alert'}
 */
export function normalizeAwarenessBadgeSeverity(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim().toLowerCase();
  if (['info', 'informational', 'none', 'default', 'low', 'blue'].includes(s)) return '';
  if (['warning', 'warn', 'medium', 'amber', 'yellow'].includes(s)) return 'warning';
  if (['alert', 'error', 'critical', 'danger', 'high', 'red'].includes(s)) return 'alert';
  return '';
}

/**
 * @param {Record<string, unknown>} entry
 * @returns {boolean}
 */
function awarenessEntryHasSeverityField(entry) {
  if (!entry || typeof entry !== 'object') return false;
  return ['badge_severity', 'severity', 'badge_tone', 'alert_level'].some((k) =>
    Object.prototype.hasOwnProperty.call(entry, k)
  );
}

/**
 * `awareness_queries` del procedimiento: prioridad lista embebida en `procedures_list`; si vacío, bloque raíz.
 * @param {Record<string, unknown>} session
 * @param {string} procInstanceName
 * @returns {unknown[]}
 */
function collectAwarenessQueriesArrayForProcedure(session, procInstanceName) {
  if (!session || !procInstanceName) return [];
  const procedures = getSessionProceduresArray(session);
  if (Array.isArray(procedures)) {
    const proc = procedures.find((p) => procInstanceNameMatches(p, procInstanceName));
    const aq = proc?.awareness_queries ?? proc?.awareness_data;
    if (Array.isArray(aq) && aq.length > 0) return aq;
  }
  const blocks = Array.isArray(session.procedure_awareness_queries)
    ? session.procedure_awareness_queries
    : Array.isArray(session.awareness_indicators)
      ? session.awareness_indicators
      : null;
  if (Array.isArray(blocks)) {
    const procEntry = blocks.find((p) => procInstanceNameMatches(p, procInstanceName));
    const aq = procEntry?.awareness_queries;
    if (Array.isArray(aq)) return aq;
  }
  return [];
}

/**
 * Una entrada por cada query con el mismo `procedure_view` (p. ej. dos filas `iconbuttonup` → dos badges).
 * Respeta {@link awarenessBadgeVisibilityMatches} (p. ej. `badge_visibility_rules` o rowCount > 0 por defecto).
 * Orden = orden en `awareness_queries`.
 *
 * @param {string} procInstanceName
 * @param {string} lpFrontendPageName
 * @returns {Array<{ count: number, tone: ''|'warning'|'alert', tooltip: string }>}
 */
export function getAwarenessIconStripBadgeDescriptors(procInstanceName, lpFrontendPageName) {
  if (!procInstanceName || !lpFrontendPageName) return [];
  const memo = getAwarenessMemoBuckets().iconStrip;
  const mk = awarenessMemoKeyProcLp(procInstanceName, lpFrontendPageName);
  if (memo.has(mk)) return memo.get(mk) ?? [];
  try {
    const us = getParsedUserSessionFromStorage();
    const want = String(lpFrontendPageName).trim().toLowerCase();
    const queries = collectAwarenessQueriesArrayForProcedure(us, procInstanceName);
    /** @type {Array<{ count: number, tone: ''|'warning'|'alert', tooltip: string }>} */
    const out = [];
    for (const entry of queries) {
      if (!entry || typeof entry !== 'object') continue;
      if (awarenessEntryProcedureView(entry) !== want) continue;
      if (!awarenessBadgeVisibilityMatches(entry)) continue;
      const count = awarenessEntryDataLength(entry);
      let tone = 'warning';
      if (awarenessEntryHasSeverityField(entry)) {
        const raw =
          entry.badge_severity ??
          entry.severity ??
          entry.badge_tone ??
          entry.alert_level;
        tone = normalizeAwarenessBadgeSeverity(raw);
      }
      const nameHint =
        (entry.query_name && String(entry.query_name).trim()) ||
        (entry.name && String(entry.name).trim()) ||
        (entry.id != null ? `#${entry.id}` : '');
      const tooltip = nameHint || lpFrontendPageName;
      out.push({ count, tone, tooltip: String(tooltip) });
    }
    memo.set(mk, out);
    return out;
  } catch (_) {
    memo.set(mk, []);
    return [];
  }
}

/**
 * Entradas awareness visibles del strip (mismo orden que {@link getAwarenessIconStripBadgeDescriptors}).
 * @param {string} procInstanceName
 * @param {string} lpFrontendPageName
 * @returns {Record<string, unknown>[]}
 */
export function getAwarenessIconStripEntries(procInstanceName, lpFrontendPageName) {
  if (!procInstanceName || !lpFrontendPageName) return [];
  try {
    const us = getParsedUserSessionFromStorage();
    const want = String(lpFrontendPageName).trim().toLowerCase();
    const queries = collectAwarenessQueriesArrayForProcedure(us, procInstanceName);
    /** @type {Record<string, unknown>[]} */
    const out = [];
    for (const entry of queries) {
      if (!entry || typeof entry !== 'object') continue;
      if (awarenessEntryProcedureView(entry) !== want) continue;
      if (!awarenessBadgeVisibilityMatches(entry)) continue;
      out.push(entry);
    }
    return out;
  } catch (_) {
    return [];
  }
}

/**
 * Destino de navegación al pulsar el bell/badge del strip iconbuttonup/down.
 * Preferencia: target explícito en la query → match por query_name vs icons → heurística samples → primer icono.
 *
 * @param {object|null|undefined} proc
 * @param {'up'|'down'} which
 * @param {number} [badgeIndex]
 * @returns {{ view: object, filterName: string|null, reason: string }|null}
 */
export function resolveIconStripAwarenessNavTarget(proc, which, badgeIndex = 0) {
  if (!proc || typeof proc !== 'object') return null;
  const pin = proc.procInstanceName ?? proc.name;
  if (!pin) return null;
  const lp = which === 'down' ? AWARENESS_VIEW_ICONBUTTON_DOWN : AWARENESS_VIEW_ICONBUTTON_UP;
  const stripIcons = which === 'down'
    ? (Array.isArray(proc.icons_down) ? proc.icons_down : [])
    : (Array.isArray(proc.icons_up) ? proc.icons_up : []);
  const defs = Array.isArray(proc.new_definition) ? proc.new_definition : [];
  const pool = stripIcons.length ? stripIcons : defs;

  const findByLp = (want) => {
    const w = String(want || '').trim().toLowerCase();
    if (!w || w === AWARENESS_VIEW_ICONBUTTON_UP || w === AWARENESS_VIEW_ICONBUTTON_DOWN) return null;
    return (
      pool.find((v) => String(v?.lp_frontend_page_name || v?.viewName || '').trim().toLowerCase() === w) ||
      null
    );
  };

  const entries = getAwarenessIconStripEntries(String(pin), lp);
  const entry = entries[badgeIndex] || entries[0] || null;
  if (entry) {
    const explicit =
      entry.target_view ??
      entry.open_view ??
      entry.navigate_to_view ??
      entry.linked_view ??
      entry.view_name ??
      null;
    const hit = findByLp(explicit);
    if (hit) {
      return {
        view: hit,
        filterName: hit.lp_frontend_page_filter ?? null,
        reason: 'explicit-target'
      };
    }
    const qn = String(entry.query_name || entry.name || '').trim().toLowerCase();
    if (qn) {
      const byName = pool.find((v) => {
        const name = String(v?.lp_frontend_page_name || v?.viewName || '').trim().toLowerCase();
        return name && (qn.includes(name) || name.includes(qn.replace(/_/g, '')));
      });
      if (byName) {
        return {
          view: byName,
          filterName: byName.lp_frontend_page_filter ?? null,
          reason: 'query-name-match'
        };
      }
      if (/sample/.test(qn)) {
        const sampleView =
          findByLp('LogSamples') ||
          pool.find((v) => /sample/i.test(String(v?.lp_frontend_page_name || v?.viewName || '')));
        if (sampleView) {
          return {
            view: sampleView,
            filterName: sampleView.lp_frontend_page_filter ?? null,
            reason: 'samples-heuristic'
          };
        }
      }
    }
  }
  if (pool[0]) {
    return {
      view: pool[0],
      filterName: pool[0].lp_frontend_page_filter ?? null,
      reason: 'first-strip-icon'
    };
  }
  return null;
}

/**
 * Tono para una vista (`procedure_view` = lp): peor severidad entre queries que coinciden.
 * Sin campos de severidad en ninguna entrada coincidente → `'warning'` (por defecto UI).
 * Si todas las entradas explícitas son `info` → `''` (azul).
 *
 * @param {string} procInstanceName
 * @param {string} lpFrontendPageName
 * @returns {''|'warning'|'alert'}
 */
export function getAwarenessBadgeToneForLp(procInstanceName, lpFrontendPageName) {
  if (!procInstanceName || !lpFrontendPageName) return 'warning';
  const memo = getAwarenessMemoBuckets().tone;
  const mk = awarenessMemoKeyProcLp(procInstanceName, lpFrontendPageName);
  if (memo.has(mk)) return memo.get(mk) ?? 'warning';
  try {
    const us = getParsedUserSessionFromStorage();
    const want = String(lpFrontendPageName).trim().toLowerCase();

    /** @type {unknown[]} */
    const entries = [];
    const procedures = getSessionProceduresArray(us);
    const proc = Array.isArray(procedures)
      ? procedures.find((p) => procInstanceNameMatches(p, procInstanceName))
      : null;
    const aqEmb = proc?.awareness_queries ?? proc?.awareness_data;
    if (Array.isArray(aqEmb)) {
      for (const e of aqEmb) {
        if (e && awarenessEntryProcedureView(e) === want && awarenessBadgeVisibilityMatches(e)) {
          entries.push(e);
        }
      }
    }
    const blocks = Array.isArray(us.procedure_awareness_queries)
      ? us.procedure_awareness_queries
      : Array.isArray(us.awareness_indicators)
        ? us.awareness_indicators
        : null;
    if (Array.isArray(blocks)) {
      const procEntry = blocks.find((p) => procInstanceNameMatches(p, procInstanceName));
      const aq2 = procEntry?.awareness_queries;
      if (Array.isArray(aq2)) {
        for (const e of aq2) {
          if (e && awarenessEntryProcedureView(e) === want && awarenessBadgeVisibilityMatches(e)) {
            entries.push(e);
          }
        }
      }
    }

    if (entries.length === 0) {
      memo.set(mk, 'warning');
      return 'warning';
    }

    const tones = entries.map((e) => {
      if (!awarenessEntryHasSeverityField(e)) return undefined;
      const raw = e.badge_severity ?? e.severity ?? e.badge_tone ?? e.alert_level;
      return normalizeAwarenessBadgeSeverity(raw);
    });
    const defined = tones.filter((t) => t !== undefined);
    if (defined.length === 0) {
      memo.set(mk, 'warning');
      return 'warning';
    }
    const out = defined.reduce((a, b) => worseBadgeTone(a, b));
    memo.set(mk, out);
    return out;
  } catch (_) {
    memo.set(mk, 'warning');
    return 'warning';
  }
}

/**
 * Misma agrupación de LPs que el contador: severidad máxima entre vista base y sub-vistas.
 *
 * @param {string} procInstanceName
 * @param {Record<string, unknown>|null|undefined} def
 * @returns {''|'warning'|'alert'}
 */
export function getAwarenessBadgeToneForMenuDefinition(procInstanceName, def) {
  if (!procInstanceName || !def) return 'warning';
  const subs = getSubViewIconList(def);
  const baseLp = resolveItemLpFrontendPageName(def);
  if (subs.length === 0) {
    return baseLp ? getAwarenessBadgeToneForLp(procInstanceName, baseLp) : 'warning';
  }
  const seen = new Set();
  /** @type {(''|'warning'|'alert')[]} */
  const tones = [];
  if (baseLp) {
    seen.add(String(baseLp).toLowerCase());
    tones.push(getAwarenessBadgeToneForLp(procInstanceName, baseLp));
  }
  for (const raw of subs) {
    const sub = normalizeMenuSubViewEntry(raw, def);
    const lp = sub?.lp_frontend_page_name || baseLp;
    const dedupe = String(lp || '').toLowerCase();
    if (!lp || seen.has(dedupe)) continue;
    seen.add(dedupe);
    tones.push(getAwarenessBadgeToneForLp(procInstanceName, lp));
  }
  if (tones.length === 0) return 'warning';
  return tones.reduce((a, b) => worseBadgeTone(a, b));
}
